import { useEffect, useRef } from 'react';
import { useSubscription } from '@apollo/client';
import { BOT_STREAM_RECEIVED } from '../graphql/subscriptions';
import { useChatStore } from '../stores/chatStore';
import { streamingAnswerRef } from './streamingAnswerRef';
import { safeStreamEnd, StreamTagBalancer, wrapStreamChars } from '../utils/streamHtml';

interface BotStreamEvent {
  type: 'stage' | 'article_selected' | 'answer_delta' | 'done';
  stage?: 'search_kb' | 'read_articles' | 'generate';
  publicId?: string;
  title?: string;
  text?: string;
  answer?: string;
  images?: string[];
  files?: string[];
  transferredToExperts?: boolean;
}

/**
 * Target animation window (ms). Backend deltas arrive in large chunks once per ~500ms;
 * this loop drains the buffer into the DOM over roughly this window so the user sees
 * continuous typewriter motion at 60+ fps instead of 2 fps.
 */
const DRAIN_WINDOW_MS = 260;
const MIN_CHARS_PER_FRAME = 1;

export function useBotStreamSubscription() {
  const { setStreamStage, addStreamArticle, clearStream, setTyping, markBotStreamed } = useChatStore();

  // Incremental append: fullBuffer grows with every delta; committedLen is what's already in DOM.
  const fullBufferRef = useRef('');
  const committedLenRef = useRef(0);
  const rafRef = useRef<number | null>(null);
  const lastFrameRef = useRef<number>(0);
  const balancerRef = useRef<StreamTagBalancer>(new StreamTagBalancer());

  const resetStreamState = (): void => {
    fullBufferRef.current = '';
    committedLenRef.current = 0;
    lastFrameRef.current = 0;
    balancerRef.current.reset();
    const host = streamingAnswerRef.el;
    if (host) host.innerHTML = '';
  };

  const syncHostHeight = (host: HTMLElement): void => {
    // Let CSS transition animate the height change on new lines.
    host.style.height = `${host.scrollHeight}px`;
  };

  const pinScroll = (): void => {
    const scrollHost = streamingAnswerRef.scrollHost;
    if (!scrollHost) return;
    const threshold = 80;
    const nearBottom = scrollHost.scrollHeight - scrollHost.scrollTop - scrollHost.clientHeight < threshold;
    if (nearBottom) scrollHost.scrollTop = scrollHost.scrollHeight;
  };

  const step = (now: number): void => {
    rafRef.current = null;
    const host = streamingAnswerRef.el;
    if (!host) {
      rafRef.current = requestAnimationFrame(step);
      return;
    }

    const totalSafe = safeStreamEnd(fullBufferRef.current);
    const pending = totalSafe - committedLenRef.current;
    if (pending <= 0) {
      lastFrameRef.current = 0;
      // More bytes may still be pending inside an open tag — retry if fullBuffer moved ahead.
      if (fullBufferRef.current.length > committedLenRef.current) {
        rafRef.current = requestAnimationFrame(step);
      }
      return;
    }

    const elapsed = lastFrameRef.current === 0 ? 16 : now - lastFrameRef.current;
    lastFrameRef.current = now;

    // Adaptive pace: empty the safe tail within ~DRAIN_WINDOW_MS. Slight jitter keeps the
    // cadence organic — otherwise a 1–2 char/frame loop reads as mechanical tapping.
    const baseRate = Math.ceil((pending * elapsed) / DRAIN_WINDOW_MS);
    const jitter = Math.round((Math.random() - 0.5) * 2);
    const perFrame = Math.max(MIN_CHARS_PER_FRAME, baseRate + jitter);
    // Critical: clamp nextEnd to a tag boundary *below* committedLen + perFrame, not just
    // below totalSafe — otherwise the chunk can end mid-tag (e.g. slicing `</li>` into
    // `</l` + `i>`), and wrapStreamChars will treat the leading `<` as a text char.
    let nextEnd = safeStreamEnd(fullBufferRef.current, committedLenRef.current + perFrame);
    if (nextEnd <= committedLenRef.current) {
      // The typewriter budget landed inside a tag. Grab the whole tag in one frame so the
      // loop keeps progressing; the tag renders without fade, which is fine for a handful
      // of characters.
      const gt = fullBufferRef.current.indexOf('>', committedLenRef.current);
      if (gt === -1) {
        // Tag not complete yet — wait for more bytes.
        lastFrameRef.current = 0;
        if (fullBufferRef.current.length > committedLenRef.current) {
          rafRef.current = requestAnimationFrame(step);
        }
        return;
      }
      nextEnd = gt + 1;
    }

    const chunk = fullBufferRef.current.slice(committedLenRef.current, nextEnd);
    committedLenRef.current = nextEnd;

    if (chunk) {
      const balanced = balancerRef.current.push(chunk);
      host.insertAdjacentHTML('beforeend', wrapStreamChars(balanced));
      syncHostHeight(host);
      pinScroll();
    }

    if (committedLenRef.current < fullBufferRef.current.length) {
      rafRef.current = requestAnimationFrame(step);
    } else {
      lastFrameRef.current = 0;
    }
  };

  const scheduleDrain = (): void => {
    if (rafRef.current !== null) return;
    rafRef.current = requestAnimationFrame(step);
  };

  useEffect(
    () => () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      resetStreamState();
      rafRef.current = null;
    },
    [],
  );

  const { error } = useSubscription<{ botStreamReceived: BotStreamEvent }>(BOT_STREAM_RECEIVED, {
    onData: ({ data }) => {
      const event = data?.data?.botStreamReceived;
      if (!event) return;

      switch (event.type) {
        case 'stage':
          if (event.stage) {
            if (event.stage === 'search_kb') resetStreamState();
            setStreamStage(event.stage);
            setTyping(true);
          }
          break;
        case 'article_selected':
          if (event.title) {
            addStreamArticle(event.title);
          }
          break;
        case 'answer_delta':
          if (event.text) {
            fullBufferRef.current += event.text;
            scheduleDrain();
          }
          break;
        case 'done': {
          // Flush any remaining tail (including any still-open tag, appended raw) so the
          // visible draft matches the full answer before the messageReceived replacement.
          const host = streamingAnswerRef.el;
          if (host) {
            const tail = fullBufferRef.current.slice(committedLenRef.current);
            if (tail) {
              const balanced = balancerRef.current.push(tail);
              host.insertAdjacentHTML('beforeend', wrapStreamChars(balanced));
              syncHostHeight(host);
              pinScroll();
            }
            committedLenRef.current = fullBufferRef.current.length;
          }
          if (rafRef.current !== null) {
            cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
          }
          // Mark so the imminent messageReceived doesn't re-animate already-visible text.
          markBotStreamed();
          clearStream();
          setTyping(false);
          // Buffer cleared on next stage='search_kb' or unmount — keep DOM text visible until replacement.
          break;
        }
      }
    },
    onError: (err) => {
      console.error('Bot stream subscription error:', err);
    },
  });

  return { error };
}
